import { useSelector } from "react-redux";
import {
  agenciesSelector,
  filteredContactRolesSelector,
  originatorRoleIdSelector,
  softwareListSelector,
} from "../selectors/entities";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useRequest } from "redux-query-react";
import counties from "./data/counties.json";
import cities from "./data/cities.json";
import {
  getAgenciesQuery,
  getContactRolesQuery,
  getSoftwareListQuery,
} from "../actions/queries";
import { STANDARD_MODEL_TYPES } from "../utils/modelTypeValue";

const softwareNames = [
  "HEC-RAS",
  "HEC-HMS",
  "HEC-GeoHMS",
  "HEC-WAT",
  "HEC-EFM",
  "HEC-MetVue",
  "HEC-SSP",
  "RiverWare",
  "Infoworks ICM",
  "SWMM",
  "InfoSWMM",
  "XPSWMM",
  "XPStorm",
  "ICPR",
  "OpenFlows StormCAD",
  "OpenFlows CivilStorm",
  "OpenFlows FLOOD",
  "OpenFlows CulvertMaster",
  "ADCIRC",
  "WHAFIS",
  "SWAN",
  "MIKE 21",
  "FEMA Hazus",
  "FEMA BCA",
  "HEC-FDA",
  "HEC-FIA",
  "Hydro-CAD",
  "HY-8 Culvert",
  "Delft3D",
  "SWAT",
  "PRMS",
  "WRAP",
  "Other",
  "Unknown",
];

const softwareDistributors = [
  "US Army Corps of Engineers",
  "Center for Advanced Decision Support for Water and Environmental Systems, University of Colorado",
  "Innovyze",
  "Environmental Protection Agency",
  "Streamline Technologies",
  "Bentley",
  "University of North Carolina",
  "Federal Emergency Management Agency",
  "Delft University of Technology",
  "DHI",
  "Deltares",
  "Texas A&M University",
  "US Geological Survey",
  "Other",
  "Unknown",
];

export const softwareInfoByDistributor = {
  "HEC-RAS": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-ras/",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-HMS": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-hms/",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-GeoHMS": {
    distributionUrl: "https://www.hec.usace.army.mil/software/",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-WAT": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-wat/    ",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-EFM": {
    distributionUrl: "https://www.hec.usace.army.mil/software/",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-MetVue": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-metvue/",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-SSP": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-ssp/",
    distributor: "US Army Corps of Engineers",
  },
  RiverWare: {
    distributionUrl: "https://www.riverware.org",
    distributor:
      "Center for Advanced Decision Support for Water and Environmental Systems, University of Colorado",
  },
  "Infoworks ICM": {
    distributionUrl: "https://www.innovyze.com/en-us/products/infoworks-icm",
    distributor: "Innovyze",
  },
  SWMM: {
    distributionUrl:
      "https://www.epa.gov/water-research/storm-water-management-model-swmm",
    distributor: "Environmental Protection Agency",
  },
  InfoSWMM: {
    distributionUrl: "https://www.innovyze.com/en-us/products/infoswmm",
    distributor: "Innovyze",
  },
  XPSWMM: {
    distributionUrl: "https://www.innovyze.com/en-us/products/xpswmm",
    distributor: "Innovyze",
  },
  XPStorm: {
    distributionUrl: "https://www.innovyze.com/en-us/products/xpstorm",
    distributor: "Innovyze",
  },
  ICPR: {
    distributionUrl: "https://streamnologies.com/content/",
    distributor: "Streamline Technologies",
  },
  "OpenFlows StormCAD": {
    distributionUrl:
      "https://www.bentley.com/en/products/product-line/hydraulics-and-hydrology-software/stormcad",
    distributor: "Bentley",
  },
  "OpenFlows CivilStorm": {
    distributionUrl:
      "https://www.bentley.com/en/products/product-line/hydraulics-and-hydrology-software/civilstorm",
    distributor: "Bentley",
  },
  "OpenFlows FLOOD": {
    distributionUrl:
      "https://www.bentley.com/en/products/product-line/hydraulics-and-hydrology-software/openflows-flood",
    distributor: "Bentley",
  },
  "OpenFlows CulvertMaster": {
    distributionUrl:
      "https://www.bentley.com/en/products/product-line/hydraulics-and-hydrology-software/culvertmaster",
    distributor: "Bentley",
  },
  ADCIRC: {
    distributionUrl: "https://adcirc.org",
    distributor: "University of North Carolina",
  },
  WHAFIS: {
    distributionUrl: "https://www.fema.gov/flood-maps/software",
    distributor: "Federal Emergency Management Agency",
  },
  SWAN: {
    distributionUrl: "https://swanmodel.sourceforge.io",
    distributor: "Delft University of Technology",
  },
  "MIKE 21": {
    distributionUrl:
      "https://www.mikepoweredbydhi.com/products/mike-21/hydrodynamics",
    distributor: "DHI",
  },
  "FEMA Hazus": {
    distributionUrl: "https://www.fema.gov/flood-maps/products-tools/hazus",
    distributor: "Federal Emergency Management Agency",
  },
  "FEMA BCA": {
    distributionUrl:
      "https://www.fema.gov/grants/guidance-tools/benefit-cost-analysis ",
    distributor: "Federal Emergency Management Agency",
  },
  "HEC-FDA": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-fda/ ",
    distributor: "US Army Corps of Engineers",
  },
  "HEC-FIA": {
    distributionUrl: "https://www.hec.usace.army.mil/software/hec-fia/",
    distributor: "US Army Corps of Engineers",
  },
  "Hydro-CAD": {
    distributionUrl: "https://www.hydrocad.net/",
    distributor: "",
  },
  "HY-8 Culvert": {
    distributionUrl:
      "https://www.fhwa.dot.gov/engineering/hydraulics/software/hy8/",
    distributor: "",
  },
  Delft3D: {
    distributionUrl: "https://oss.deltares.nl/web/delft3d",
    distributor: "Deltares",
  },
  SWAT: {
    distributionUrl: "https://swat.tamu.edu/software/",
    distributor: "Texas A&M University",
  },
  PRMS: {
    distributionUrl:
      "https://www.usgs.gov/software/precipitation-runoff-modeling-system-prms",
    distributor: "US Geological Survey",
  },
  WRAP: {
    distributionUrl: "https://wrap.engr.tamu.edu/",
    distributor: "",
  },
  Other: {
    distributionUrl: "",
    distributor: "Other",
  },
  Unknown: {
    distributionUrl: "",
    distributor: "Unknown",
  },
};
export const rbfsRegionsOptions = [
  {
    label: "1 - EAST",
    value: "EAST",
  },
  {
    label: "2 - WEST",
    value: "WEST",
  },
  {
    label: "3 - CENTRAL",
    value: "CENTRAL",
  },
  {
    label: "4 - LRGV",
    value: "LRGV",
  },
];

export const twdbFloodPlanningRegionIdOptions = [
  {
    label: "1 - Canadian-Upper Red",
    value: "01",
  },
  {
    label: "2 - Lower Red-Sulphur-Cypress",
    value: "02",
  },
  {
    label: "3 - Trinity",
    value: "03",
  },
  {
    label: "4 - Sabine",
    value: "04",
  },
  {
    label: "5 - Neches",
    value: "05",
  },
  {
    label: "6 - San Jacinto",
    value: "06",
  },
  {
    label: "7 - Upper Brazos",
    value: "07",
  },
  {
    label: "8 - Lower Brazos",
    value: "08",
  },
  {
    label: "9 - Upper Colorado",
    value: "09",
  },
  {
    label: "10 - Lower Colorado-Lavaca",
    value: "10",
  },
  {
    label: "11 - Guadalupe",
    value: "11",
  },
  {
    label: "12 - San Antonio",
    value: "12",
  },
  {
    label: "13 - Nueces",
    value: "13",
  },
  {
    label: "14 - Upper Rio Grande",
    value: "14",
  },
  {
    label: "15 - Lower Rio Grande",
    value: "15",
  },
];

const updateFrequency = [
  "Realtime",
  "Near-Realtime",
  "Hourly",
  "Daily",
  "Weekly",
  "Monthly",
  "Quarterly",
  "Biannually",
  "Annually",
  "2 or More Years",
  "None",
  "Other",
  "Unknown",
];

const useConstraint = [
  "Public",
  "Restricted – Can share with partner agencies only",
  "Restricted - Display on Web",
  "Restricted - Requires DUA",
  "Restricted - No Display on Web",
  "Other",
];

const modelTypes = STANDARD_MODEL_TYPES;

const modelCategories = [
  "Alternative Analysis Model",
  "Baseline Model",
  "Other",
];

const baselineModels = [];

const collectionIdToRegionIdMapping = {
  161: "01",
  117: "02",
  163: "03",
  164: "04",
  116: "05",
  118: "06",
  165: "07",
  166: "08",
  167: "09",
  120: "10",
  119: "11",
  168: "12",
  169: "13",
  170: "14",
  121: "15",
};

const collectionIdToNameMapping = {
  116: "Emergency Management Data Supporting TX-21215 Severe Weather Event",
  117: "Flood Model Ver 3.3",
  118: "Flood Model Ver 3.6",
  119: "Modeling, Flood Event TX-20180425",
  120: "Model, Tsnunami Event TX-20220103",
  121: "Modeling for Severe Weather Flood TX-20200512",
  161: "Region 01 Collection",
  163: "Region 03 Collection",
  164: "Region 04 Collection",
  165: "Region 07 Collection",
  166: "Region 08 Collection",
  167: "Region 09 Collection",
  168: "Region 12 Collection",
  169: "Region 13 Collection",
  170: "Region 14 Collection",
};

export const minIOUserByRegionId = {
  "01": "tdis-twdb-region-01",
  "02": "tdis-twdb-region-02",
  "03": "tdis-twdb-region-03",
  "04": "tdis-twdb-region-04",
  "05": "tdis-twdb-region-05",
  "06": "tdis-twdb-region-06",
  "07": "tdis-twdb-region-07",
  "08": "tdis-twdb-region-08",
  "09": "tdis-twdb-region-09",
  10: "tdis-twdb-region-10",
  11: "tdis-twdb-region-11",
  12: "tdis-twdb-region-12",
  13: "tdis-twdb-region-13",
  14: "tdis-twdb-region-14",
  15: "tdis-twdb-region-15",
};

// TODO: Will probably want to not make this hardcoded
export const minIOPasswordByRegionId = {
  "01": "d77N5KbSzLYZ68K9",
  "02": "cGtugq9dZLe4qwKX",
  "03": "UHCDSbvKJd9vhHnF",
  "04": "5tNphgeEWU5ZFYZz",
  "05": "2KJVMXnKgBqGj9BX",
  "06": "gDM7W6kb3atfB6jL",
  "07": "fxA2XCCesu8LyWJU",
  "08": "CLeYWs3RZSsRkYkX",
  "09": "xuq5yaUkR4x9h8JG",
  10: "tdAEXNDQabsBxe9k",
  11: "N8mFNba7K9F4D8Bx",
  12: "ajcQ6weNSAyG7crw",
  13: "azmuaFTCpAtRqHwy",
  14: "7dVwAPKYWBm35wrg",
  15: "jvBrk2ftvrfSma85",
};

// Temp mapping until we get an actual mapping
export const getRegionIdFromCollectionId = (collectionId) => {
  let regionId = collectionIdToRegionIdMapping[collectionId];
  // since "collectionId" is sometimes a name ...
  if (!regionId) {
    let trueCollectionId = Object.keys(collectionIdToNameMapping).find(
      (key) => collectionIdToNameMapping[key] === collectionId,
    );
    regionId = collectionIdToRegionIdMapping[trueCollectionId];
  }
  return regionId;
};

// Temp mapping until we get an actual mapping
export const getCollectionIdFromRegion = (regionId) =>
  Object.keys(collectionIdToRegionIdMapping).find(
    (key) => collectionIdToRegionIdMapping[key] === regionId,
  );

export const getOptionFromValue = (value) => ({ value, label: value });
export const getOptionsFromList = (values) => {
  const options = Array.isArray(values)
    ? values
        .filter((val) => val !== "") // Filter out empty strings
        .map((val) => {
          return { value: val, label: val };
        })
    : [];
  return options;
};

export const getIndexOptionsFromList = (values) => {
  return Array.isArray(values)
    ? values.map((val, idx) => ({ value: idx, label: val }))
    : [];
};

export const countyOptions = getOptionsFromList(counties);
export const cityOptions = getOptionsFromList(cities);
export const useConstraintOptions = getOptionsFromList(useConstraint);
export const modelTypeOptions = getOptionsFromList(modelTypes);
export const modelCategoryOptions = getOptionsFromList(modelCategories);
export const baselineModelOptions = getOptionsFromList(baselineModels);
export const updateFrequencyOptions = getOptionsFromList(updateFrequency);
export const softwareNameOptions = getOptionsFromList(softwareNames);
export const softwareDistributorOptions =
  getOptionsFromList(softwareDistributors);
// export const roleOptions = getOptionsFromList(roles);
// export const agencyOptions = getIndexOptionsFromList(agencies);
// export const twdbFloodPlanningRegionIdOptions = getOptionsFromList(regions);

export const getRegionOptionFromValue = (regionId) =>
  twdbFloodPlanningRegionIdOptions.find((opt) => opt.value === regionId);
// export const getAgencyOptionFromValue = (agencyId) => agencyId ? agencyOptions[agencyId] : null

const useFetchedEntityOptions = ({
  selector,
  getQuery,
  toOption,
  resolveEntity,
}) => {
  const { accessToken } = useCurrentUser();
  const entities = useSelector(selector);
  const [{ isFinished }] = useRequest(
    accessToken && entities.length === 0 ? getQuery(accessToken) : null,
  );

  const options = entities.map(toOption);
  const getOptionFromValue = (value) => {
    const entity = resolveEntity(entities, value);
    if (!entity || Object.keys(entity).length === 0) {
      return { label: "", value: value ?? "" };
    }
    return toOption(entity);
  };

  return [options, getOptionFromValue, entities.length > 0 || isFinished];
};

const getLabelFromSoftwareObj = ({ software_name, software_abbr }) => {
  let label = `${software_name} (${software_abbr})`;
  // If the name & abbreviation are the same, just show the name
  if (software_name === software_abbr) {
    label = software_name;
  }
  if (software_abbr === "Unknown/Other") {
    label = "Unknown/Other";
  }
  return label;
};

export const useSoftwareNameOptions = () =>
  useFetchedEntityOptions({
    selector: softwareListSelector,
    getQuery: getSoftwareListQuery,
    toOption: (software) => ({
      label: getLabelFromSoftwareObj(software),
      value: software.software_abbr,
    }),
    resolveEntity: (softwareList, value) =>
      softwareList.find(
        ({ software_abbr, software_name }) =>
          software_abbr === value || software_name === value,
      ) || {},
  });

// Agency Example: {id: 898, abbreviation: "4803696", name: "Archer City"}
export const useAgencyOptions = () =>
  useFetchedEntityOptions({
    selector: agenciesSelector,
    getQuery: getAgenciesQuery,
    toOption: (agency) => ({
      label: agency.name,
      value: agency.id,
    }),
    resolveEntity: (agencies, value) =>
      agencies.find(({ id }) => id === value) ||
      agencies.find(({ name }) => name === value) ||
      {},
  });

// Agency Example: {"id":4,"name":"Maintainer","description":"Contact responsible for updating ....","metadata_level":"TBD"}
export const useContactRoleOptions = () => {
  const { accessToken } = useCurrentUser();
  const roles = useSelector(filteredContactRolesSelector);
  const originatorRoleId = useSelector(originatorRoleIdSelector);
  const [{ isFinished }] = useRequest(
    accessToken && roles.length === 0
      ? getContactRolesQuery(accessToken)
      : null,
  );

  const options = roles.map((role, idx) => {
    return {
      label: role.name,
      value: role.id,
    };
  });

  const getRoleOptionFromValue = (value) => {
    const roleObj = roles.find(({ id }) => id === value) || {};
    return {
      label: roleObj.name,
      value,
    };
  };

  const getRoleNameFromId = (roleId) => {
    const roleObj = roles.find(({ id }) => id === roleId) || {};
    if (roleId === originatorRoleId) {
      return "originator, or digital object creator";
    }
    if (roleObj) {
      return roleObj.name;
    }
    return null;
  };

  return [
    options,
    getRoleOptionFromValue,
    roles.length > 0 || isFinished,
    getRoleNameFromId,
  ];
};
